import React, { useState, useEffect } from 'react';
import { updateTimeEntry } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatTime } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Save, Clock, Calendar, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Dialog pro editaci časového záznamu.
 * 
 * Pravidla pro validaci:
 * - Pokud jsou vyplněny start_time a end_time, duration_minutes se přepočítá automaticky
 * - end_time musí být >= start_time
 * - duration_minutes musí být >= 1
 * - billable flag může měnit pouze admin
 */
const TimeEntryEditDialog = ({ entry, open, onOpenChange, onEntryUpdated }) => {
  const { user } = useAuth();
  const isAdmin = user?.is_admin;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    date: '',
    duration_minutes: '',
    start_time: '',
    end_time: '',
    billable: true,
  });
  const [useTimeRange, setUseTimeRange] = useState(false);

  // Reset form when entry changes
  useEffect(() => {
    if (entry) {
      const hasTimeRange = entry.start_time && entry.end_time;
      setUseTimeRange(hasTimeRange);
      
      // Parse ISO datetime to time input format (HH:MM)
      let startTime = '';
      let endTime = '';
      if (entry.start_time) {
        try {
          const dt = new Date(entry.start_time);
          startTime = dt.toTimeString().slice(0, 5);
        } catch (e) { /* ignore parse error */ }
      }
      if (entry.end_time) {
        try {
          const dt = new Date(entry.end_time);
          endTime = dt.toTimeString().slice(0, 5);
        } catch (e) { /* ignore parse error */ }
      }
      
      setFormData({
        description: entry.description || '',
        date: entry.date || '',
        duration_minutes: entry.duration_minutes?.toString() || '',
        start_time: startTime,
        end_time: endTime,
        billable: entry.billable !== false, // Default to true
      });
    }
  }, [entry]);

  // Calculate duration from time range
  const calculateDurationFromRange = (startTime, endTime, date) => {
    if (!startTime || !endTime || !date) return null;
    
    try {
      const start = new Date(`${date}T${startTime}`);
      const end = new Date(`${date}T${endTime}`);
      const diffMs = end - start;
      if (diffMs < 0) return null;
      return Math.max(1, Math.round(diffMs / 60000));
    } catch (e) {
      return null;
    }
  };

  // Auto-update duration when time range changes
  useEffect(() => {
    if (useTimeRange && formData.start_time && formData.end_time && formData.date) {
      const calculated = calculateDurationFromRange(
        formData.start_time,
        formData.end_time,
        formData.date
      );
      if (calculated !== null) {
        setFormData(prev => ({ ...prev, duration_minutes: calculated.toString() }));
      }
    }
  }, [useTimeRange, formData.start_time, formData.end_time, formData.date]);

  const handleSave = async () => {
    // Validace
    if (!formData.date) {
      toast.error('Datum je povinné');
      return;
    }
    
    const duration = parseInt(formData.duration_minutes);
    if (!duration || duration < 1) {
      toast.error('Doba trvání musí být alespoň 1 minuta');
      return;
    }
    
    // Validace časového rozsahu
    if (useTimeRange && formData.start_time && formData.end_time) {
      const start = new Date(`${formData.date}T${formData.start_time}`);
      const end = new Date(`${formData.date}T${formData.end_time}`);
      if (end < start) {
        toast.error('Čas konce musí být po začátku');
        return;
      }
    }

    setLoading(true);
    try {
      const updateData = {
        description: formData.description || null,
        date: formData.date,
        duration_minutes: duration,
      };
      
      // Přidej start_time a end_time pouze pokud jsou vyplněny
      if (useTimeRange && formData.start_time && formData.end_time) {
        updateData.start_time = `${formData.date}T${formData.start_time}:00`;
        updateData.end_time = `${formData.date}T${formData.end_time}:00`;
      }
      
      // Admin může měnit billable flag
      if (isAdmin) {
        updateData.billable = formData.billable;
      }

      await updateTimeEntry(entry.id, updateData);
      toast.success('Záznam aktualizován');
      onOpenChange(false);
      if (onEntryUpdated) onEntryUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při ukládání');
    } finally {
      setLoading(false);
    }
  };

  if (!entry) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-oswald text-xl uppercase tracking-tight">
            Upravit záznam
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Project info (read-only) */}
          <div className="bg-gray-50 p-3 border border-gray-200">
            <div className="font-oswald text-sm font-bold">{entry.project_name}</div>
            {entry.task_title && (
              <div className="text-xs text-gray-500 font-serif">{entry.task_title}</div>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="font-oswald uppercase text-xs tracking-wide">Popis</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="border-2 border-gray-200 focus:border-black rounded-none"
              placeholder="Popis práce..."
              data-testid="edit-entry-description"
            />
          </div>

          {/* Date */}
          <div className="space-y-2">
            <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
              <Calendar size={12} /> Datum *
            </Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="border-2 border-gray-200 focus:border-black rounded-none"
              data-testid="edit-entry-date"
            />
          </div>

          {/* Time Range Toggle */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="use-time-range"
              checked={useTimeRange}
              onChange={(e) => setUseTimeRange(e.target.checked)}
              className="rounded border-gray-300"
            />
            <Label htmlFor="use-time-range" className="font-serif text-sm cursor-pointer">
              Zadat časový rozsah (start → konec)
            </Label>
          </div>

          {/* Time Range Inputs */}
          {useTimeRange && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Začátek</Label>
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="border-2 border-gray-200 focus:border-black rounded-none"
                  data-testid="edit-entry-start-time"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Konec</Label>
                <Input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="border-2 border-gray-200 focus:border-black rounded-none"
                  data-testid="edit-entry-end-time"
                />
              </div>
            </div>
          )}

          {/* Duration */}
          <div className="space-y-2">
            <Label className="font-oswald uppercase text-xs tracking-wide flex items-center gap-1">
              <Clock size={12} /> Doba trvání (minuty) *
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="1"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                className="border-2 border-gray-200 focus:border-black rounded-none flex-1"
                disabled={useTimeRange}
                data-testid="edit-entry-duration"
              />
              <span className="text-sm text-gray-500 font-serif">
                = {formatTime(parseInt(formData.duration_minutes) || 0)}
              </span>
            </div>
            {useTimeRange && (
              <p className="text-xs text-gray-500 font-serif">
                Doba se automaticky vypočítá z časového rozsahu
              </p>
            )}
          </div>

          {/* Billable Toggle (Admin only) */}
          {isAdmin && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="billable-toggle"
                checked={formData.billable}
                onChange={(e) => setFormData({ ...formData, billable: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="billable-toggle" className="font-serif text-sm cursor-pointer flex items-center gap-1">
                <DollarSign size={12} className={formData.billable ? 'text-green-600' : 'text-gray-400'} />
                {formData.billable ? 'Fakturovatelný čas' : 'Nefakturovatelný čas'}
              </Label>
            </div>
          )}

          {/* Non-billable indicator for non-admin */}
          {!isAdmin && !formData.billable && (
            <div className="flex items-center gap-1 text-sm text-gray-500 font-serif">
              <DollarSign size={12} className="text-gray-400" />
              Nefakturovatelný čas
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t border-gray-200">
            <Button
              onClick={handleSave}
              disabled={loading}
              className="flex-1 bg-black text-white font-oswald uppercase tracking-wide rounded-none hover:bg-gray-800"
              data-testid="edit-entry-save"
            >
              <Save size={16} className="mr-2" />
              Uložit změny
            </Button>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="font-oswald uppercase rounded-none border-2 border-gray-200"
            >
              Zrušit
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TimeEntryEditDialog;
